import React, { useState } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  FieldTextInput,
  PrimaryButton,
  H4,
  Form,
  ValidationError,
  Modal,
  SecondaryButton,
  Toast,
} from '../../../components';
import api from '../../../api';
import {
  composeValidators,
  emailFormatValid,
  maxLength,
  numericOnly,
  required,
} from '../../../util/validators';
import { useToast } from '../../../hooks/useToast';

import css from './SectionContactRequest.module.css';

const SectionContactRequest = ({ listingId, onManageDisableScrolling }) => {
  const intl = useIntl();
  const { toastMessage, showToast, closeToast } = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = async values => {
    setSubmitError(null);
    setSubmitInProgress(true);

    try {
      await api.listings.contactListingOwner(listingId, {
        contactType: values.contactType,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        email: values.email,
        message: values.message,
        companyName: values.companyName || '',
        role: values.role || '',
      });

      setIsOpen(false);

      showToast(
        intl.formatMessage({
          id: 'ContactRequestForm.success',
        })
      );
    } catch (error) {
      console.error('Failed to send contact request:', error);
      setSubmitError(
        intl.formatMessage({
          id: 'General.error',
        })
      );
    } finally {
      setSubmitInProgress(false);
    }
  };

  const closeModal = form => {
    if (form) form.reset();
    setIsOpen(false);

    setSubmitError(null);
  };

  return (
    <div className={css.section}>
      {toastMessage && <Toast message={toastMessage} onClose={closeToast} intl={intl} />}

      <PrimaryButton onClick={() => setIsOpen(true)}>
        <FormattedMessage id="ContactRequestForm.button" />
      </PrimaryButton>

      <Modal
        id="ContactDealerModal"
        isOpen={isOpen}
        onClose={() => closeModal()}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.modalContent}>
          <H4 as="h2" className={css.title}>
            <FormattedMessage id="ContactRequestForm.button" />
          </H4>

          <FinalForm
            key={isOpen ? 'form-open' : 'form-closed'}
            onSubmit={handleSubmit}
            initialValues={{ contactType: 'company' }}
            render={({ handleSubmit, submitting, values, form }) => (
              <Form onSubmit={handleSubmit} className={css.form}>
                <div className={css.contactTypeGroup}>
                  {['company', 'private'].map(type => (
                    <label
                      key={type}
                      className={`${css.radioLabel} ${
                        values.contactType === type ? css.radioLabelActive : ''
                      }`}
                    >
                      <Field name="contactType" component="input" type="radio" value={type} />
                      {type === 'private' ? (
                        <FormattedMessage id="ContactRequestForm.private" />
                      ) : (
                        <FormattedMessage id="ContactRequestForm.company" />
                      )}
                    </label>
                  ))}
                </div>

                <div className={css.name}>
                  <FieldTextInput
                    id="firstName"
                    name="firstName"
                    type="text"
                    label={intl.formatMessage({ id: 'ContactRequestForm.firstName' })}
                    isRequired
                    validate={required(intl.formatMessage({ id: 'General.fieldRequired' }))}
                  />
                  <FieldTextInput
                    id="lastName"
                    name="lastName"
                    type="text"
                    label={intl.formatMessage({ id: 'ContactRequestForm.lastName' })}
                    isRequired
                    validate={required(intl.formatMessage({ id: 'General.fieldRequired' }))}
                  />
                </div>
                <FieldTextInput
                  id="email"
                  name="email"
                  type="email"
                  label={intl.formatMessage({ id: 'ContactRequestForm.email' })}
                  isRequired
                  validate={composeValidators(
                    required(intl.formatMessage({ id: 'General.fieldRequired' })),
                    emailFormatValid(intl.formatMessage({ id: 'General.emailInvalid' }))
                  )}
                />
                <FieldTextInput
                  id="phoneNumber"
                  name="phoneNumber"
                  type="text"
                  label={intl.formatMessage({ id: 'ContactRequestForm.phone' })}
                  isRequired
                  validate={composeValidators(
                    required(intl.formatMessage({ id: 'General.fieldRequired' })),
                    numericOnly(intl.formatMessage({ id: 'General.numericOnly' })),
                    maxLength(
                      intl.formatMessage({ id: 'General.maxLengthValidation' }, { maxLength: 20 }),
                      20
                    )
                  )}
                />

                {values.contactType === 'company' && (
                  <div className={css.companyFields}>
                    <FieldTextInput
                      id="companyName"
                      name="companyName"
                      type="text"
                      label={intl.formatMessage({ id: 'ContactRequestForm.company' })}
                      isRequired
                    />
                    <FieldTextInput
                      id="role"
                      name="role"
                      type="text"
                      label={intl.formatMessage({ id: 'ContactRequestForm.role' })}
                    />
                  </div>
                )}

                <FieldTextInput
                  id="message"
                  name="message"
                  type="textarea"
                  label={intl.formatMessage({ id: 'ContactRequestForm.message' })}
                  isRequired
                  validate={composeValidators(
                    required(intl.formatMessage({ id: 'General.fieldRequired' })),
                    maxLength(
                      intl.formatMessage(
                        { id: 'General.maxLengthValidation' },
                        { maxLength: 1000 }
                      ),
                      1000
                    )
                  )}
                />

                {submitError && (
                  <ValidationError fieldMeta={{ touched: true, error: submitError }} />
                )}

                <div className={css.actions}>
                  <SecondaryButton type="button" onClick={() => closeModal(form)}>
                    <FormattedMessage id="General.cancel" />
                  </SecondaryButton>
                  <PrimaryButton
                    type="submit"
                    inProgress={submitInProgress || submitting}
                    disabled={submitInProgress || submitting}
                  >
                    <FormattedMessage id="ContactRequestForm.submit" />
                  </PrimaryButton>
                </div>
              </Form>
            )}
          />
        </div>
      </Modal>
    </div>
  );
};

export default SectionContactRequest;
